/**
 * AdMob — Android-app monetisation (Capacitor wrapper).
 *
 * Adukoo runs as both a website (AdSense) AND a Capacitor Android app
 * (AdMob). Google policy forbids using AdSense inside a WebView, so this
 * module ONLY runs on `Capacitor.isNativePlatform()` and is the single
 * choke-point for every mobile-app ad surface.
 *
 * Configuration lives in /app/frontend/.env (public-safe identifiers, all
 * documented in the AdMob console under your app's "Ad units" tab):
 *
 *   REACT_APP_ADMOB_BANNER_ID         — banner ad unit ID
 *   REACT_APP_ADMOB_INTERSTITIAL_ID   — full-screen interstitial ad unit ID
 *
 * The App ID (ca-app-pub-XXXXXXXX~YYYYYYY) is set inside Android Studio's
 * AndroidManifest.xml — see /app/memory/admob_setup.md for the snippet.
 *
 * If the env vars are blank we fall back to Google's official TEST unit
 * IDs so QA / dev builds always render labelled "Test Ad" creatives
 * (no risk of invalid-traffic strikes).
 *
 * Public API:
 *   initAdMob()                  → boots SDK + handles UMP consent. Call once on app start.
 *   showHomeBanner()             → anchored adaptive banner at the bottom of the screen.
 *   hideBanner()                 → tear down the banner when leaving an ad-friendly page.
 *   maybeShowInterstitial()      → preloads on first call; shows after every N detail views.
 *
 * Every function is a NO-OP on the web — safe to call from shared React code.
 */

// Google's universal TEST IDs — see https://developers.google.com/admob/android/test-ads
const TEST_BANNER_ID = "ca-app-pub-3940256099942544/6300978111";
const TEST_INTERSTITIAL_ID = "ca-app-pub-3940256099942544/1033173712";

const INTERSTITIAL_FREQUENCY = 6; // show after every Nth listing-detail view

let _initPromise = null;
let _bannerShown = false;
let _interstitialReady = false;
let _interstitialLoading = false;
let _detailViewCount = 0;

async function _isNative() {
    try {
        const { Capacitor } = await import("@capacitor/core").catch(() => ({}));
        return !!(Capacitor && typeof Capacitor.isNativePlatform === "function" && Capacitor.isNativePlatform());
    } catch {
        return false;
    }
}

async function _getAdMob() {
    try {
        const mod = await import("@capacitor-community/admob");
        return mod;
    } catch (e) {
        // Plugin not bundled into this build (web-only ship) — silently disable.
        return null;
    }
}

/** One-time SDK init + UMP consent flow. Safe to call multiple times. */
export async function initAdMob() {
    if (_initPromise) return _initPromise;
    _initPromise = (async () => {
        if (!(await _isNative())) return false;
        const m = await _getAdMob();
        if (!m) return false;
        const { AdMob, AdmobConsentStatus } = m;
        try {
            await AdMob.initialize({
                requestTrackingAuthorization: true,
                initializeForTesting: process.env.NODE_ENV !== "production",
            });
            // UMP consent — required for EEA/UK users. The console-configured
            // form decides what (if anything) gets shown; non-EEA users see nothing.
            const info = await AdMob.requestConsentInfo();
            if (info?.isConsentFormAvailable && info.status === AdmobConsentStatus.REQUIRED) {
                await AdMob.showConsentForm();
            }
            return true;
        } catch (e) {
            // SDK init failures must never crash the app — ads are optional.
            // eslint-disable-next-line no-console
            console.warn("[AdMob] init failed", e);
            return false;
        }
    })();
    return _initPromise;
}

/** Show the bottom-anchored adaptive banner. Idempotent. */
export async function showHomeBanner() {
    if (_bannerShown) return;
    const ready = await initAdMob();
    if (!ready) return;
    const m = await _getAdMob();
    if (!m) return;
    const { AdMob, BannerAdSize, BannerAdPosition } = m;
    const adId = process.env.REACT_APP_ADMOB_BANNER_ID || TEST_BANNER_ID;
    try {
        await AdMob.showBanner({
            adId,
            adSize: BannerAdSize.ADAPTIVE_BANNER,
            position: BannerAdPosition.BOTTOM_CENTER,
            margin: 0,
            isTesting: !process.env.REACT_APP_ADMOB_BANNER_ID, // env-blank ⇒ test mode
        });
        _bannerShown = true;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[AdMob] showBanner failed", e);
    }
}

/** Hide / remove the banner. Idempotent. */
export async function hideBanner() {
    if (!_bannerShown) return;
    const m = await _getAdMob();
    if (!m) return;
    try {
        await m.AdMob.hideBanner();
        await m.AdMob.removeBanner();
    } catch { /* swallow */ }
    _bannerShown = false;
}

async function _preloadInterstitial() {
    if (_interstitialReady || _interstitialLoading) return;
    const ready = await initAdMob();
    if (!ready) return;
    const m = await _getAdMob();
    if (!m) return;
    const adId = process.env.REACT_APP_ADMOB_INTERSTITIAL_ID || TEST_INTERSTITIAL_ID;
    _interstitialLoading = true;
    try {
        await m.AdMob.prepareInterstitial({
            adId,
            isTesting: !process.env.REACT_APP_ADMOB_INTERSTITIAL_ID,
        });
        _interstitialReady = true;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[AdMob] prepareInterstitial failed", e);
    } finally {
        _interstitialLoading = false;
    }
}

/**
 * Trigger an interstitial after every Nth listing-detail view. Call this
 * from ListingDetail.jsx's useEffect on mount. The first call preloads
 * silently; the Nth call shows and immediately starts preloading the next.
 */
export async function maybeShowInterstitial() {
    if (!(await _isNative())) return;
    _detailViewCount += 1;
    // First-time priming — pull an ad into memory but don't disrupt the user.
    if (_detailViewCount === 1) {
        _preloadInterstitial();
        return;
    }
    if (_detailViewCount % INTERSTITIAL_FREQUENCY !== 0) return;
    if (!_interstitialReady) {
        // Not ready yet — try to preload and skip this slot rather than block.
        _preloadInterstitial();
        return;
    }
    const m = await _getAdMob();
    if (!m) return;
    try {
        await m.AdMob.showInterstitial();
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[AdMob] showInterstitial failed", e);
    } finally {
        _interstitialReady = false;
        _preloadInterstitial(); // queue the next one
    }
}
